import { Data } from '../../models/data';
import { IOrder, IOrderItemModifier } from "../../models/order.interface";
import { Util } from '../../utils/util';
import {
    IDataItem, IDataCoupon, IDataCouponItem, IDataFundraiser, IDataTax, IDataZipTax, IItemTax, IItemPrice,
    IItemModifierDisplayGroup, IItemModifierDisplayGroupModifier, IItemModifierDisplayGroupModifierPrice,
    IDataPreModifier, ICouponItemCategory
} from '../../models/data.interface';
import {
    ICartData, ISourceData, IDataCoupon as IDataCouponLib, IAppAdapter, AppType, ICouponDeps, IItemPriceRequest
} from 'coupon-ts-lib';
import { User } from '../../models/user';
import { OnlineOrderingUtil } from '../../online-ordering/online-ordering-util';

export class CouponAdapter implements IAppAdapter {
    private readonly _data: Data;
    private readonly _user: User;
    private readonly _isDevelopmentMode: boolean;
    private readonly _map = <T, U>(xs: T[] | null | undefined, fn: (x: T) => U): U[] => xs?.map(fn) ?? [];
    constructor(data: Data, user: User, isDevelopmentMode: boolean = false) {
        this._data = data;
        this._user = user;
        this._isDevelopmentMode = isDevelopmentMode;
    }

    public bootstrap() {
        const appType = AppType.OLO;
        const sourceData: ISourceData = this._toEngineSource(this._data);
        const dependencies: ICouponDeps = {
            nowStore: Util.NowStore,
            getBusDate: Util.GetBUSDate,
            getItemPrice: this._getItemPrice,
            isDevelopmentMode: () => this._isDevelopmentMode
        }
        return { appType, sourceData, dependencies };
    }

    /**
     * Prices a menu item at an arbitrary size for the coupon engine, which has no pricing rules of
     * its own. Used by the "Upgrade" coupon type to find what the item costs at the size it
     * upgrades to. Always returns a whole-item price, never a half's share.
     */
    private readonly _getItemPrice = (request: IItemPriceRequest): number => {
        const mItem = this._data.getProperty("Items").find((x: IDataItem) => x.ItemId === request.itemId);
        if (!mItem?.Prices)
            return 0;

        const modifiers = request.modifiers.map(modifier => ({
            ModifierId: modifier.modifierId,
            ModifierDisplayGroupId: modifier.modifierDisplayGroupId,
            PreModifierId: modifier.preModifierId,
            IsDefault: modifier.isDefault,
            CountForPricing: modifier.countForPricing,
            CountForPricingByItself: modifier.countForPricingByItself,
            Price: modifier.price
        })) as unknown as IOrderItemModifier[];

        const price = OnlineOrderingUtil.GetItemPrice(
            mItem,
            request.sizeId,
            modifiers,
            request.priceGroupId,
            this._data.getProperty("DefaultPriceGroupId"),
            this._data.getProperty("Settings").NCDG,
            this._data.getProperty("PreModifiers")
        ).Price;

        return Math.abs(price);
    }

    public static readonly toEngineCart = (input: IOrder | null): ICartData => {
        return {
            orderTypeId: input?.OrderTypeID ?? -1,
            subtotal: input?.SubTotal ?? 0,
            discount: input?.Discount ?? 0,
            total: input?.Total ?? 0,
            futureDate: input?.FutureDate ?? null,
            futureMinute: input?.FutureMinute ?? 0,
            items: input?.Items.map(item => ({
                id: item.ItemId,
                afterTaxDiscount: item.AfterTaxDiscount,
                beforeTaxDiscount: item.BeforeTaxDiscount,
                discountedMarkedQuantity: item.DiscountedMarkedQuantity,
                discountedQuantity: item.DiscountedQuantity,
                loyaltyDiscountsAmount: item.LoyaltyDiscountAmount,
                loyaltyDiscountsQTY: item.LoyaltyDiscountsQTY,
                nonLoyaltyDiscountsQTY: item.NonLoyaltyDiscountsQTY,
                halfCount: item.HalfCount || 0,
                halfIndex: item.HalfIndex || 0,
                halfHalfPrice: item.HalfHalfPrice || 0,
                isRemoved: item.IsRemoved || false,
                isRedeemed: item.IsRedeemed || false,
                itemKey: item.ItemKey,
                itemRecId: item.ItemRecId,
                menuPrice: item.MenuPrice,
                modifiers: item.Modifiers.map(modifier => ({
                    modifierId: modifier.ModifierId,
                    modifierDisplayGroupId: modifier.ModifierDisplayGroupId,
                    preModifierId: modifier.PreModifierId,
                    isDefault: modifier.IsDefault,
                    countForPricing: modifier.CountForPricing,
                    countForPricingByItself: modifier.CountForPricingByItself,
                    price: modifier.Price
                })) || [],
                price: item.Price,
                quantity: item.Quantity,
                sizeId: item.SizeId
            })) || [],
            coupons: input?.Coupons.map(coupon => ({
                couponId: coupon.CouponId,
                couponKey: coupon.CouponKey,
                amount: coupon.Amount,
                isReward: coupon.RewardId !== null && coupon.RewardId !== undefined
            })) || [],
            itemsCoupons: input?.ItemsCoupons.map(itemCoupon => ({
                couponKey: itemCoupon.CouponKey,
                beforeTaxDiscount: itemCoupon.BeforeTaxDiscount,
                afterTaxDiscount: itemCoupon.AfterTaxDiscount,
                itemKey: itemCoupon.ItemKey,
                itemRecId: itemCoupon.ItemRecId,
                halfIndex: itemCoupon.HalfIndex
            })) || [],
            orderTypeSubType: {
                orderTypeId: input?.OrderTypeSubType.OrderTypeId ?? 0,
                orderTypeSubId: input?.OrderTypeSubType.OrderTypeSubId ?? 0,
                priceGroupId: input?.OrderTypeSubType.PriceGroupId ?? 0,
                orderTypeCharge: input?.OrderTypeSubType.OrderTypeCharge ?? 0
            },
            fundraiserId: input?.Fundraiser?.ID ?? null
        }
    }

    private readonly _toEngineSource = (input: Data): ISourceData => {
        // cache commonly used props
        const items = input.getProperty("Items");
        const taxes = input.getProperty("Taxes");
        const zipTaxes = input.getProperty("ZipTaxes");
        const settingsRaw = input.getProperty("Settings");
        const preModifiers = input.getProperty("PreModifiers");
        const coupons = input.getProperty("Coupons");
        const fundraisers = input.getProperty("Fundraisers");

        // leaf mappers
        const mapPrice = (price: IItemPrice) => ({
            minimumPrice: price.MinimumPrice,
            maximumPrice: price.MaximumPrice,
            priceGroupId: price.PriceGroupId,
            perModifierPrice: price.PerModifierPrice,
            price: price.Price,
            sizeId: price.SizeId,
            taxIncluded: price.TaxIncluded,
        });

        const mapTax = (tax: IItemTax) => ({
            taxId: tax.TaxId,
            index: tax.Index,
        });

        const mapModifierPrice = (p: IItemModifierDisplayGroupModifierPrice) => ({
            sizeId: p.SizeId,
            addOnPrice: p.AddOnPrice,
        });

        const mapModifier = (m: IItemModifierDisplayGroupModifier) => ({
            addsToPrice: m.AddsToPrice,
            countForPricing: m.CountForPricing,
            countForPricingByItself: m.CountForPricingByItself,
            isDefault: m.IsDefault,
            index: m.Index,
            isHiddenOnOrder: m.IsHiddenOnOrder,
            modifierId: m.ModifierId,
            name: m.Name,
            mergedModifierDisplayGroupId: m.MergedModifierDisplayGroupId,
            modifierWebCategoryId: m.ModifierWebCategoryId,
            modifierWebCategoryIndex: m.ModifierWebCategoryIndex,
            prices: this._map(m.Prices, mapModifierPrice),
            preModifierException: m.PreModifierException ?? [],
            sortingIndex: m.SortingIndex,
        });

        const mapModifierDisplayGroup = (g: IItemModifierDisplayGroup) => ({
            index: g.Index,
            modifierDisplayGroupId: g.ModifierDisplayGroupId,
            modifiers: this._map(g.Modifiers, mapModifier),
        });

        const mapItem = (item: IDataItem) => ({
            itemId: item.ItemId,
            index: item.Index,
            itemCategoryId: item.ItemCategoryId,
            webCategoryId: item.WebCategoryId,
            webCategoryIndex: item.WebCategoryIndex,
            subCategoryId: item.SubCategoryId,
            subCategoryIndex: item.SubCategoryIndex,
            isDefault: item.IsDefault,
            isHalfable: item.IsHalfable,
            isBuildYourOwn: item.IsBuildYourOwn,
            isLoyalty: item.IsLoyalty,
            names: item.Names,
            descriptions: item.Descriptions,
            modifierDisplayGroups: this._map(item.ModifierDisplayGroups, mapModifierDisplayGroup),
            prices: this._map(item.Prices, mapPrice),
            taxes: this._map(item.Taxes, mapTax),
        });

        const mapFundraiser = (f: IDataFundraiser) => ({
            fundraiserId: f.FundraiserId,
            startDateTime: f.StartDateTime,
            endDateTime: f.EndDateTime,
            code: f.Code,
            isCouponAllowed: f.IsCouponAllowed,
        });

        const mapZipTax = (z: IDataZipTax) => ({
            taxId: z.TaxId,
            fixAmount: z.FixAmount,
            rate: z.Rate,
            sizeId: z.SizeId,
            storeId: z.StoreId,
            zipCode: z.ZipCode,
        });

        const mapPreModifier = (pm: IDataPreModifier) => ({
            preModifierId: pm.PreModifierId,
            inventoryMultiplier: pm.InventoryMultiplier,
            isDefault: pm.IsDefault,
            isNone: pm.IsNone,
            isHidden: pm.IsHidden,
            isDeleted: pm.IsDeleted,
            priceCountMultiplier: pm.PriceCountMultiplier,
        });

        return {
            items: this._map(items, mapItem),
            taxes: this._map(taxes, (t: IDataTax) => ({
                taxId: t.TaxId,
                fixAmount: t.FixAmount,
                isPercentageBase: t.IsPercentageBase,
                isFixedAmountBase: t.IsFixedAmountBase,
                isSizeBase: t.IsSizeBase,
                isAddToSalesAmount: t.IsAddToSalesAmount,
                isAffectedByDiscounts: t.IsAffectedByDiscounts,
                isPerOrder: t.IsPerOrder,
                isNextToItemIndividually: t.IsNextToItemIndividually,
                rate: t.Rate,
                sizeId: t.SizeId,
                taxTypeId: t.TaxTypeId,
                orderTypesExcluded: t.OrderTypesExcluded,
            })),
            zipTaxes: this._map(zipTaxes, mapZipTax),
            settings: {
                BSM: settingsRaw.BSM ?? 0,
                CPNLIM: settingsRaw.CPNLIM ?? 0,
                HHP: settingsRaw.HHP ?? 0,
                ISCC: settingsRaw.ISCC ?? false,
            },
            preModifiers: this._map(preModifiers, mapPreModifier),
            coupons: this._map(coupons, (c: IDataCoupon) => CouponAdapter.createCouponDTO(c)),
            timeOffset: input.getProperty("TimeOffset") ?? 0,
            fundraisers: this._map(fundraisers, mapFundraiser),
            storeInfo: input.getProperty("StoreInfo"),
            defaultPriceGroupId: input.getProperty("DefaultPriceGroupId") ?? 0,
            tempNow: localStorage.tempNow ? new Date(localStorage.tempNow) : null,
            isLoggedIn: (this._user.isLoggedIn() && this._user.getProperty("ProfileId") != 0)
        };
    }

    public static readonly createCouponDTO = (coupon: IDataCoupon): IDataCouponLib => {
        return {
            allowOtherCoupons: coupon.AllowOtherCoupons,
            autoApplyExactMatch: coupon.AutoApplyExactMatch,
            couponId: coupon.CouponId,
            couponTypeId: coupon.CouponTypeId,
            discount: coupon.Discount,
            expiryDate: coupon.EndDate ? new Date(coupon.EndDate) : null,
            orderTypeIds: coupon.OrderTypeSubTypes.map(x => x.OrderTypeId).flat(),
            exactMatch: coupon.ExactMatch,
            excludeLoyaltyUsage: coupon.ExcludeLoyaltyUsage ?? false,
            isBeforeTax: coupon.IsBeforeTax,
            isQualifiedItemsRequired: coupon.IsVerificationNeeded,
            isProfileRequired: coupon.IsProfileRequired,
            items: coupon.Items.map((x: IDataCouponItem) => ({
                addOnPrice: x.AddOnPrice,
                groupIndex: x.GroupIndex,
                itemId: x.ItemId,
                itemIndex: x.ItemIndex,
                modifierCount: x.ModifierCount,
                quantity: x.Quantity,
                sizeId: x.SizeId
            })) || [],
            itemCategoryIds: coupon.ItemCategories.map((x: ICouponItemCategory) => x.CategoryId) || [],
            limitsPerOrder: coupon.LimitsPerOrder,
            maximumDiscountAmount: coupon.MaximumDiscountAmount,
            minimumOrderAmount: coupon.MinimumOrderAmount,
            minimumItemQuantity: coupon.MinimumItemQuantity,
            calculationMethodID: coupon.CalculationMethodID,
            startDate: coupon.StartDate ? new Date(coupon.StartDate) : null,
            schedules: coupon.Schedules?.map(x => ({
                weekDayNumber: x.WeekDayNumber,
                isActive: x.IsActive,
                startDayMinute: x.StartDayMinute,
                endDayMinute: x.EndDayMinute
            })) || [],
            upgrades: coupon.Upgrades?.map(x => ({
                buySizeId: x.BuySizeId,
                priceSizeId: x.PriceSizeId,
                upgradeIndex: x.UpgradeIndex
            })) || [],
        };
    }
}